import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight as ChevronRightIcon,
  User as UserIcon,
  SlidersHorizontal,
  Shield,
  Users as UsersIcon,
  Link2,
  CreditCard,
  Camera,
  Headphones,
  ShieldCheck,
  Check,
  CalendarDays,
  CheckCircle2,
  Star,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { db, storage } from "../firebase";
import featureGatingService from "../services/featureGatingService";
import SubscriptionPlansConfig from "../config/subscriptionPlansConfig";
import { processRazorpayPayment } from "../utils/razorpayPayment";

import SelectMenu from "../components/crm/SelectMenu";
// Format a Date (or Firestore Timestamp) as "30 Dec 2026" for the billing panel.
const fmtBillingDate = (value) => {
  if (!value) return "—";
  const d = value?.toDate ? value.toDate() : value instanceof Date ? value : new Date(value);
  if (isNaN(d?.getTime?.())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Pencil SVG icon
const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// Check SVG icon
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Blue verified tick
const BlueTick = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-500 verified-tick" viewBox="0 0 24 24" fill="currentColor">
    <path fillRule="evenodd" d="M12 2a10 10 0 100 20A10 10 0 0012 2zm4.707 7.293a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L11 13.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

// ---- Light "Account Settings" layout helpers (mockup) ----------------------
const ACC_INPUT_CLS =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

const AccField = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>
    {children}
  </div>
);

const AccToggle = ({ on, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-6 w-12 rounded-full p-1 transition-colors duration-200 ${on ? "bg-yellow-500" : "bg-slate-300"}`}
  >
    <div
      className={`h-4 w-4 rounded-full bg-white transition-transform duration-200 ${on ? "translate-x-6" : "translate-x-0"}`}
    />
  </button>
);

const ACCOUNT_SECTIONS = [
  { key: "profile", label: "Profile Information", Icon: UserIcon },
  { key: "preferences", label: "Preferences", Icon: SlidersHorizontal, lawyerOnly: true },
  { key: "security", label: "Security", Icon: Shield },
  { key: "department", label: "Access to Department", Icon: UsersIcon, lawyerOnly: true },
  { key: "connected", label: "Connected Accounts", Icon: Link2 },
  { key: "billing", label: "Billing & Plan", Icon: CreditCard },
];

// Editable field component
const EditableField = ({ label, name, value, type = "text", editingField, setEditingField, onChange, disabled = false, children }) => {
  const isEditing = editingField === name;
  const hasAsterisk = label.endsWith("*");
  const labelText = hasAsterisk ? label.slice(0, -1) : label;
  return (
    <div>
      <label className="block text-gray-400 mb-1 text-sm">{labelText}{hasAsterisk && <span className="text-red-500">*</span>}</label>
      <div className="flex items-center gap-2">
        {disabled ? (
          <span className="verified-email bg-gray-500/20 rounded-lg py-2 px-4 text-gray-400 flex-1 text-sm cursor-not-allowed min-h-[38px]">{value || "—"}</span>
        ) : isEditing ? (
          <>
            {children || (
              <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                autoFocus
                className="bg-transparent border border-yellow-500 rounded-lg py-2 px-4 text-gray-300 flex-1 text-sm focus:outline-none"
              />
            )}
            <button
              onClick={() => setEditingField(null)}
              className="text-green-400 hover:text-green-300 flex-shrink-0"
              title="Done"
            >
              <CheckIcon />
            </button>
          </>
        ) : (
          <>
            <span className="bg-gray-500/20 rounded-lg py-2 px-4 text-gray-300 flex-1 text-sm min-h-[38px]">
              {value || <span className="text-gray-500">—</span>}
            </span>
            <button
              onClick={() => setEditingField(name)}
              className="text-gray-400 hover:text-yellow-400 flex-shrink-0 transition-colors"
              title={`Edit ${label}`}
            >
              <PencilIcon />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Editable Select Field
const EditableSelect = ({ label, name, value, options, editingField, setEditingField, onChange }) => {
  const isEditing = editingField === name;
  const hasAsterisk = label.endsWith("*");
  const labelText = hasAsterisk ? label.slice(0, -1) : label;
  return (
    <div>
      <label className="block text-gray-400 mb-1 text-sm">{labelText}{hasAsterisk && <span className="text-red-500">*</span>}</label>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <SelectMenu
              name={name}
              value={value}
              onChange={onChange}
              autoFocus
              className="bg-transparent border border-yellow-500 rounded-lg py-2 px-4 text-gray-300 flex-1 text-sm focus:outline-none [&>option]:bg-gray-900 [&>option]:text-white"
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </SelectMenu>
            <button
              onClick={() => setEditingField(null)}
              className="text-green-400 hover:text-green-300 flex-shrink-0"
              title="Done"
            >
              <CheckIcon />
            </button>
          </>
        ) : (
          <>
            <span className="bg-gray-500/20 rounded-lg py-2 px-4 text-gray-300 flex-1 text-sm min-h-[38px]">
              {value || <span className="text-gray-500">—</span>}
            </span>
            <button
              onClick={() => setEditingField(name)}
              className="text-gray-400 hover:text-yellow-400 flex-shrink-0 transition-colors"
              title={`Edit ${label}`}
            >
              <PencilIcon />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Phone Edit Modal
const PhoneEditModal = ({
  isOpen, onClose,
  newCountry, setNewCountry,
  newNationalNumber, setNewNationalNumber,
  phoneOtp, setPhoneOtp,
  isSendingOtp, isVerifyingOtp,
  isOtpSent,
  otpE164Number,
  onSendOtp, onVerifyOtp, onResendOtp,
  isNewPhoneValid,
}) => {
  const otpInputRefs = Array.from({ length: 6 }, () => null);

  const handleOtpBoxChange = (index, value) => {
    if (/[^0-9]/.test(value)) return;
    const otpArr = phoneOtp.padEnd(6, " ").split("");
    otpArr[index] = value;
    const newOtp = otpArr.join("").replace(/\s/g, "");
    setPhoneOtp(newOtp.slice(0, 6));
    // Auto-focus next box
    if (value && index < 5 && otpInputRefs[index + 1]) {
      otpInputRefs[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !phoneOtp[index] && index > 0 && otpInputRefs[index - 1]) {
      otpInputRefs[index - 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    setPhoneOtp(pasted);
    const focusIdx = Math.min(pasted.length, 5);
    if (otpInputRefs[focusIdx]) otpInputRefs[focusIdx].focus();
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1E1E45] p-6 rounded-xl shadow-xl w-full max-w-sm">
        <h2 className="text-lg font-bold text-yellow-500 mb-5">
          {isOtpSent ? "Enter OTP" : "Update Phone Number"}
        </h2>

        {/* Country + Number — hidden when OTP is sent */}
        {!isOtpSent && (
          <div className="mb-4">
            <label className="block text-gray-400 mb-1 text-sm">Country Code</label>
            <SelectMenu
              value={newCountry}
              onChange={(e) => setNewCountry(e.target.value)}
              disabled={isSendingOtp}
              className="bg-gray-900 rounded-lg py-2 px-3 text-white w-full text-sm mb-3 disabled:opacity-60"
            >
              {getCountries().map((c) => (
                <option key={c} value={c}>{c} (+{getCountryCallingCode(c)})</option>
              ))}
            </SelectMenu>
            <label className="block text-gray-400 mb-1 text-sm">Phone Number</label>
            <input
              type="tel"
              value={newNationalNumber}
              onChange={(e) => setNewNationalNumber(e.target.value.replace(/\D/g, ""))}
              placeholder="Enter phone number"
              disabled={isSendingOtp}
              className="bg-gray-900 rounded-lg py-2 px-3 text-white w-full text-sm disabled:opacity-60 focus:outline-none focus:ring-1 focus:ring-yellow-500"
            />
          </div>
        )}

        {/* OTP section — 6 individual boxes */}
        {isOtpSent && (
          <div className="mb-4">
            <p className="text-gray-400 text-sm mb-4">OTP sent to <span className="text-white">{otpE164Number}</span></p>
            <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
              {Array.from({ length: 6 }).map((_, i) => (
                <input
                  key={i}
                  ref={(el) => { otpInputRefs[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={phoneOtp[i] || ""}
                  onChange={(e) => handleOtpBoxChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  disabled={isVerifyingOtp}
                  className="w-10 h-12 bg-gray-900 border-2 border-gray-600 rounded-lg text-white text-center text-lg font-semibold focus:outline-none focus:border-yellow-500 disabled:opacity-60 transition-colors"
                />
              ))}
            </div>
            <button
              onClick={onResendOtp}
              disabled={isSendingOtp}
              className="mt-3 text-yellow-500 hover:text-yellow-400 text-xs underline disabled:opacity-50 block mx-auto"
            >
              {isSendingOtp ? "Resending..." : "Resend OTP"}
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-2">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md text-sm"
          >
            Cancel
          </button>
          {isOtpSent ? (
            <button
              onClick={onVerifyOtp}
              disabled={phoneOtp.length !== 6 || isVerifyingOtp}
              className="bg-yellow-500 hover:bg-yellow-400 text-black px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isVerifyingOtp ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : null}
              {isVerifyingOtp ? "Verifying..." : "Verify OTP"}
            </button>
          ) : (
            <button
              onClick={onSendOtp}
              disabled={!isNewPhoneValid() || isSendingOtp}
              className="bg-yellow-500 hover:bg-yellow-400 text-black px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSendingOtp ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : null}
              {isSendingOtp ? "Sending..." : "Send OTP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Department Access Modal — 4-digit code verification
const DepartmentAccessModal = ({ isOpen, onClose, code, setCode, onVerify, isVerifying }) => {
  const inputRefs = Array.from({ length: 4 }, () => null);

  const handleBoxChange = (index, value) => {
    if (/[^0-9]/.test(value)) return;
    const arr = code.padEnd(4, " ").split("");
    arr[index] = value;
    const next = arr.join("").replace(/\s/g, "");
    setCode(next.slice(0, 4));
    if (value && index < 3 && inputRefs[index + 1]) inputRefs[index + 1].focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0 && inputRefs[index - 1]) {
      inputRefs[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    setCode(pasted);
    const focusIdx = Math.min(pasted.length, 3);
    if (inputRefs[focusIdx]) inputRefs[focusIdx].focus();
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1E1E45] p-6 rounded-xl shadow-xl w-full max-w-sm">
        <h2 className="text-lg font-bold text-yellow-500 mb-3">Organisation Access</h2>
        <p className="text-gray-400 text-sm mb-5">
          Enter the 4-digit code you received in your invitation email.
        </p>
        <div className="flex justify-center gap-2 mb-5" onPaste={handlePaste}>
          {Array.from({ length: 4 }).map((_, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={code[i] || ""}
              onChange={(e) => handleBoxChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={isVerifying}
              className="w-12 h-14 bg-gray-900 border-2 border-gray-600 rounded-lg text-white text-center text-xl font-semibold focus:outline-none focus:border-yellow-500 disabled:opacity-60 transition-colors"
            />
          ))}
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isVerifying}
            className="border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-60 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onVerify}
            disabled={code.length !== 4 || isVerifying}
            className="bg-yellow-500 hover:bg-yellow-400 text-black px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isVerifying ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : null}
            {isVerifying ? "Verifying..." : "Verify"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Account Modal
const DELETE_REASONS = [
  "I no longer need this account",
  "I found a better alternative",
  "Privacy concerns",
  "Too many notifications",
  "I have a duplicate account",
];

const DeleteAccountModal = ({ isOpen, onClose, onConfirm, deleteReason, setDeleteReason, customReason, setCustomReason, confirmText, setConfirmText }) => {
  if (!isOpen) return null;
  const isConfirmed = confirmText.trim().toLowerCase() === "confirm";
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1E1E45] p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-yellow-500 mb-4">Delete Account</h2>
        <p className="text-gray-300 mb-5">Are you sure you want to delete your account? This action cannot be undone.</p>

        {/* Reason selection */}
        <div className="mb-4">
          <label className="block text-gray-400 mb-2 text-sm">Why are you leaving?</label>
          <div className="space-y-2">
            {DELETE_REASONS.map((reason) => (
              <label key={reason} className="flex items-center gap-2 cursor-pointer text-sm text-white">
                <input
                  type="radio"
                  name="deleteReason"
                  value={reason}
                  checked={deleteReason === reason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="accent-yellow-500"
                />
                {reason}
              </label>
            ))}
            <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
              <input
                type="radio"
                name="deleteReason"
                value="Other"
                checked={deleteReason === "Other"}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="accent-yellow-500"
              />
              Other
            </label>
          </div>
          {deleteReason === "Other" && (
            <textarea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Please tell us your reason..."
              className="bg-gray-900 rounded-lg py-2 px-3 text-white w-full text-sm mt-2 focus:outline-none focus:ring-1 focus:ring-yellow-500 resize-none"
              rows={3}
            />
          )}
        </div>

        {/* Confirm by typing "confirm" */}
        <div className="mb-4">
          <label className="block text-gray-400 mb-1 text-sm">Type <span className="text-white font-semibold">confirm</span> to delete</label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="bg-gray-900 rounded-lg py-2 px-3 text-white w-full text-sm focus:outline-none focus:ring-1 focus:ring-yellow-500"
            placeholder="confirm"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md text-sm">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!deleteReason || (deleteReason === "Other" && !customReason.trim()) || !isConfirmed}
            className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
           Delete
          </button>
        </div>
      </div>
    </div>
  );
};

function Account() {
  const { globalUser, logOut } = useAuth();
  const userId = globalUser?.uid;
  const navigate = useNavigate();
  const auth = getAuth();
  const userCollection = localStorage.getItem("userType") === "client" ? "client" : "lawyers";
  const isClient = localStorage.getItem("userType") === "client";

  const [editingField, setEditingField] = useState(null);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);

  const [settings, setSettings] = useState({
    mobileNumber: false,
    email: false,
    lawyerId: false,
    location: false,
  });
  const [googleca, setgoogleca] = useState({ googleCalender: false });
  const [formData, setFormData] = useState({
    profile: "",
    name: "",
    type: "",
    mobileNumber: "",
    userCategory: "",
    degree: "",
    lawyerId: "",
    email: "",
    address: "",
    location: "",
    firmName: "",
    representsFirm: "",
    department: "",
    collegeName: "",
    registrationNumber: "",
    about: "",
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [confirmText, setConfirmText] = useState("");

  // Department Access states
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [deptCode, setDeptCode] = useState("");
  const [isDeptVerifying, setIsDeptVerifying] = useState(false);
  const [isDepartmentVerified, setIsDepartmentVerified] = useState(false);
  const [departmentName, setDepartmentName] = useState("");

  // Phone number states
  const [newCountry, setNewCountry] = useState("IN");
  const [newNationalNumber, setNewNationalNumber] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpE164Number, setOtpE164Number] = useState("");
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      const uid = globalUser?.uid;
      if (!uid) return;
      try {
        const userDocRef = doc(db, userCollection, uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.userCateogry && !data.userCategory) data.userCategory = data.userCateogry;
          if (data.isFirm && !data.representsFirm) data.representsFirm = data.isFirm;
          setFormData((prev) => ({ ...prev, ...data }));
          if (data.settings) setSettings((prev) => ({ ...prev, ...data.settings }));
          if (data.googleca) setgoogleca((prev) => ({ ...prev, ...data.googleca }));
          if (data.mobileWithCountryCode) {
            try {
              const parsed = parsePhoneNumber(data.mobileWithCountryCode);
              if (parsed) {
                setNewCountry(parsed.country || "IN");
                setNewNationalNumber(parsed.nationalNumber || "");
              }
            } catch {
              if (data.mobileNumber) setNewNationalNumber(data.mobileNumber);
            }
          } else if (data.mobileNumber) {
            setNewNationalNumber(data.mobileNumber);
          }
          // Mark as verified if they have a verified timestamp
          if (data.mobileVerifiedAt) setIsPhoneVerified(true);
          // Email is considered verified if Firebase Auth says so (or persisted flag)
          if (auth.currentUser?.emailVerified || data.emailVerified) setIsEmailVerified(true);
          // Department access verification state
          if (data.departmentVerified) {
            setIsDepartmentVerified(true);
            setDepartmentName(data.verifiedDepartment || data.department || "");
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to load user data.");
      }
    };
    if (globalUser?.uid) {
      fetchUserData();
    }
  }, [globalUser, auth]);

  // Organisation / department access is granted (and taken away) from jr-admin,
  // so watch the user doc instead of trusting the one-time read above — when an
  // admin removes access the chip and the verify button flip without a reload.
  useEffect(() => {
    const uid = globalUser?.uid;
    if (!uid) return;
    const unsubscribe = onSnapshot(
      doc(db, userCollection, uid),
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : null;
        const verified = Boolean(data?.departmentVerified);
        setIsDepartmentVerified(verified);
        setDepartmentName(verified ? data?.verifiedDepartment || data?.department || "" : "");
        setFormData((prev) => ({
          ...prev,
          departmentVerified: verified,
          verifiedDepartment: verified ? data?.verifiedDepartment || "" : "",
          verifiedOrganisation: verified ? data?.verifiedOrganisation || "" : "",
          departmentVerifiedAt: verified ? data?.departmentVerifiedAt || null : null,
        }));
      },
      (error) => console.error("[DeptAccess] snapshot failed:", error),
    );
    return () => unsubscribe();
  }, [globalUser, userCollection]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === "lawyerId" ? value.toUpperCase() : value }));
  };

  const handleSaveChanges = async () => {
    if (!userId) { toast.error("User not identified."); return; }
    try {
      const userDocRef = doc(db, userCollection, userId);
      const saveData = { ...formData, settings, googleca, isFirm: formData.representsFirm, userCateogry: formData.userCategory };
      await setDoc(userDocRef, saveData, { merge: true });
      toast.success("Changes saved successfully!");
      setEditingField(null);
    } catch (error) {
      toast.error("Error saving changes: " + error.message);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !userId) return;
    const storageRef = ref(storage, `profiles/${userId}/${file.name}`);
    try {
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setFormData((prev) => ({ ...prev, profile: downloadURL }));
      await setDoc(doc(db, userCollection, userId), { profile: downloadURL }, { merge: true });
      toast.success("Profile image uploaded successfully!");
    } catch (error) {
      toast.error("Error uploading image: " + error.message);
    }
  };

  const openDeleteConfirmationModal = () => {
    setIsDeleteModalOpen(true);
  };

  const closeDeleteConfirmationModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteReason("");
    setCustomReason("");
    setConfirmText("");
  };

  const handleDeleteAccount = async () => {
    const user = auth.currentUser;
    if (!user || !userId) { toast.error("User not found."); closeDeleteConfirmationModal(); return; }
    try {
      const reason = deleteReason === "Other" ? customReason.trim() : deleteReason;

      // Send account deletion request emails
      const emailPayload = {
        name: formData.name || "",
        email: user.email || "",
        phoneNumber: formData.mobileNumber || "",
        reason,
      };
      await Promise.all([
        fetch("https://jurident-feedback-backend-596718606544.us-central1.run.app/send-account-deletion-admin", {
          method: "POST",
          body: JSON.stringify(emailPayload),
          headers: { "Content-Type": "application/json" },
        }),
        fetch("https://jurident-feedback-backend-596718606544.us-central1.run.app/send-account-deletion-user", {
          method: "POST",
          body: JSON.stringify({ email: user.email, name: formData.name || "" }),
          headers: { "Content-Type": "application/json" },
        }),
      ]);

      toast.success("Account deletion request submitted. You will receive a confirmation email shortly.");
      closeDeleteConfirmationModal();
    } catch (error) {
      toast.error("Failed to submit deletion request. Please try again.");
      closeDeleteConfirmationModal();
    }
  };

  const openDeptModal = () => {
    setDeptCode("");
    setIsDeptModalOpen(true);
  };

  const closeDeptModal = () => {
    if (isDeptVerifying) return;
    setIsDeptModalOpen(false);
    setDeptCode("");
  };

  const handleVerifyDeptCode = async () => {
    if (!userId) { toast.error("User not identified."); return; }
    if (deptCode.length !== 4) { toast.error("Enter the 4-digit code."); return; }
    const userEmail = (formData.email || auth.currentUser?.email || "").trim().toLowerCase();
    if (!userEmail) { toast.error("No email on this account."); return; }

    setIsDeptVerifying(true);
    try {
      const invitesRef = collection(db, "departmentInvites");
      const snap = await getDocs(
        query(
          invitesRef,
          where("email", "==", userEmail),
          where("code", "==", deptCode),
          where("verified", "==", false),
          limit(1),
        ),
      );

      if (snap.empty) {
        toast.error("Invalid code or no pending invitation for this account.");
        return;
      }

      const inviteDoc = snap.docs[0];
      const inviteData = inviteDoc.data();

      // Access removed from jr-admin — the code stays dead until an admin resets
      // the invite, otherwise the user could just re-enter it from their email.
      if (inviteData.revoked) {
        toast.error("This invitation has been revoked. Please contact your organisation admin.");
        return;
      }

      const department = inviteData.department || "";
      const organisation = inviteData.organisation || "";

      // Mark invite as verified
      await updateDoc(doc(db, "departmentInvites", inviteDoc.id), {
        verified: true,
        verifiedAt: serverTimestamp(),
        verifiedUserId: userId,
        verifiedUserCollection: userCollection,
      });

      // Mark user as department-verified
      await setDoc(
        doc(db, userCollection, userId),
        {
          departmentVerified: true,
          verifiedDepartment: department,
          verifiedOrganisation: organisation,
          departmentVerifiedAt: serverTimestamp(),
          departmentInviteId: inviteDoc.id,
        },
        { merge: true },
      );

      setFormData((prev) => ({ ...prev, verifiedDepartment: department, verifiedOrganisation: organisation, departmentVerified: true }));
      setDepartmentName(department);
      setIsDepartmentVerified(true);
      setIsDeptModalOpen(false);
      setDeptCode("");
      toast.success(`Department verified: ${department}`);
    } catch (error) {
      console.error("[DeptVerify] Failed:", error);
      toast.error("Verification failed: " + (error.message || "Unknown error"));
    } finally {
      setIsDeptVerifying(false);
    }
  };

  const handleToggleSetting = async (key) => {
    if (!userId) return;
    const newValue = !settings[key];
    setSettings((prev) => ({ ...prev, [key]: newValue }));
    try {
      await setDoc(doc(db, userCollection, userId), { settings: { ...settings, [key]: newValue } }, { merge: true });
      toast.success(`${key.replace(/([A-Z])/g, " $1").trim()} visibility updated!`);
    } catch (error) {
      toast.error("Error updating setting: " + error.message);
      setSettings((prev) => ({ ...prev, [key]: !newValue }));
    }
  };

  const handleGoogleCalendarToggle = async () => {
    if (!userId) return;
    const newValue = !googleca.googleCalender;
    setgoogleca({ googleCalender: newValue });
    const userDocRef = doc(db, userCollection, userId);
    try {
      await setDoc(userDocRef, { googleca: { googleCalender: newValue } }, { merge: true });
      if (newValue) {
        const provider = new GoogleAuthProvider();
        provider.addScope("https://www.googleapis.com/auth/calendar.events");
        provider.setCustomParameters({ access_type: "offline", prompt: "consent" });
        const result = await signInWithPopup(auth, provider);
        const credential = GoogleAuthProvider.credentialFromResult(result);
        if (credential?.accessToken) {
          localStorage.setItem("googleAccessToken", credential.accessToken);
          localStorage.setItem("googleTokenExpiry", String(Date.now() + 3600 * 1000));
          toast.success("Google Calendar connected successfully");
        } else throw new Error("Failed to obtain access token.");
      } else {
        localStorage.removeItem("googleAccessToken");
        localStorage.removeItem("googleTokenExpiry");
        toast.info("Google Calendar disconnected");
      }
    } catch (error) {
      toast.error(`Failed to ${newValue ? "connect" : "disconnect"} Google Calendar: ${error.message}`);
      setgoogleca({ googleCalender: !newValue });
      await setDoc(userDocRef, { googleca: { googleCalender: !newValue } }, { merge: true });
    }
  };

  // Phone helpers
  const getNewE164Number = () => `+${getCountryCallingCode(newCountry)}${newNationalNumber}`;
  const isNewPhoneValid = () => newNationalNumber.length > 0 && isValidPhoneNumber(getNewE164Number());

  // Same duplicate check as Flutter isMobileNumberRegistered —
  // queries both lawyers + client collections, excludes current user's own doc
  const isMobileNumberRegistered = async (nationalNumber) => {
    const currentUid = globalUser?.uid;
    console.log("[PhoneCheck] Checking if number is registered:", nationalNumber);
    console.log("[PhoneCheck] Current user UID:", currentUid);
    console.log("[PhoneCheck] Current userCollection:", userCollection);

    const lawyerSnap = await getDocs(query(collection(db, "lawyers"), where("mobileNumber", "==", nationalNumber)));
    console.log("[PhoneCheck] Lawyers docs found with this number:", lawyerSnap.docs.length);
    for (const d of lawyerSnap.docs) {
      console.log("[PhoneCheck] Lawyer doc ID:", d.id, "| Is current user?", d.id === currentUid, "| Data:", JSON.stringify({ mobileNumber: d.data().mobileNumber, mobileWithCountryCode: d.data().mobileWithCountryCode }));
      if (d.id !== currentUid) {
        console.log("[PhoneCheck] BLOCKED: Number found in lawyers collection under different user:", d.id);
        return true;
      }
    }

    const clientSnap = await getDocs(query(collection(db, "client"), where("mobileNumber", "==", nationalNumber)));
    console.log("[PhoneCheck] Client docs found with this number:", clientSnap.docs.length);
    for (const d of clientSnap.docs) {
      console.log("[PhoneCheck] Client doc ID:", d.id, "| Is current user?", d.id === currentUid, "| Data:", JSON.stringify({ mobileNumber: d.data().mobileNumber, mobileWithCountryCode: d.data().mobileWithCountryCode }));
      if (d.id !== currentUid) {
        console.log("[PhoneCheck] BLOCKED: Number found in client collection under different user:", d.id);
        return true;
      }
    }

    console.log("[PhoneCheck] Number is available (not registered by another user)");
    return false;
  };

  const handleSendOtp = async () => {
    const fullNumber = getNewE164Number();
    console.log("[SendOTP] Attempting to send OTP for:", fullNumber, "| National number:", newNationalNumber);
    console.log("[SendOTP] Current formData.mobileNumber:", formData.mobileNumber, "| formData.mobileWithCountryCode:", formData.mobileWithCountryCode);
    if (!isValidPhoneNumber(fullNumber)) { toast.error("Please enter a valid phone number."); return; }
    setIsSendingOtp(true);
    try {
      // Duplicate check before sending OTP
      const alreadyRegistered = await isMobileNumberRegistered(newNationalNumber);
      console.log("[SendOTP] isMobileNumberRegistered result:", alreadyRegistered);
      if (alreadyRegistered) {
        toast.error("This phone number is already registered with another account.");
        return;
      }
      const sendOtpFunction = httpsCallable(getFunctions(), "sendOtp");
      await sendOtpFunction({ phoneNumber: fullNumber });
      setOtpE164Number(fullNumber);
      setPhoneOtp("");
      setIsOtpSent(true);
      toast.success("OTP sent successfully!");
    } catch (error) {
      toast.error("Failed to send OTP: " + (error.message || "Unknown error"));
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpE164Number) return;
    setIsSendingOtp(true);
    try {
      await httpsCallable(getFunctions(), "sendOtp")({ phoneNumber: otpE164Number });
      toast.success("OTP resent!");
    } catch (error) {
      toast.error("Failed to resend OTP: " + (error.message || "Unknown error"));
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (phoneOtp.length !== 6) { toast.error("Please enter a valid 6-digit OTP."); return; }
    setIsVerifyingOtp(true);
    try {
      await httpsCallable(getFunctions(), "verifyOtp")({ phoneNumber: otpE164Number, otp: phoneOtp });
      const callingCode = `+${getCountryCallingCode(newCountry)}`;
      const oldNumber = formData.mobileNumber;
      console.log("[VerifyOTP] OTP verified. Updating phone number.");
      console.log("[VerifyOTP] Old number:", oldNumber, "| New number:", newNationalNumber);
      console.log("[VerifyOTP] Updating collection:", userCollection, "| Doc ID:", userId);

      // Update the current user's collection
      await updateDoc(doc(db, userCollection, userId), {
        countryCode: callingCode,
        mobileNumber: newNationalNumber,
        mobileWithCountryCode: otpE164Number,
        mobileVerifiedAt: serverTimestamp(),
      });
      console.log("[VerifyOTP] Updated", userCollection, "collection successfully");

      // Also check and clean up the OTHER collection if the user has a stale doc there
      const otherCollection = userCollection === "lawyers" ? "client" : "lawyers";
      try {
        const otherDocRef = doc(db, otherCollection, userId);
        const otherDoc = await getDoc(otherDocRef);
        if (otherDoc.exists() && otherDoc.data().mobileNumber) {
          console.log("[VerifyOTP] Found stale doc in", otherCollection, "with mobileNumber:", otherDoc.data().mobileNumber);
          await updateDoc(otherDocRef, {
            mobileNumber: newNationalNumber,
            mobileWithCountryCode: otpE164Number,
            countryCode: callingCode,
            mobileVerifiedAt: serverTimestamp(),
          });
          console.log("[VerifyOTP] Cleaned up stale doc in", otherCollection);
        }
      } catch (cleanupErr) {
        console.warn("[VerifyOTP] Could not clean up other collection (may not exist):", cleanupErr.message);
      }

      setFormData((prev) => ({ ...prev, countryCode: callingCode, mobileNumber: newNationalNumber, mobileWithCountryCode: otpE164Number }));
      setIsOtpSent(false);
      setPhoneOtp("");
      setIsPhoneVerified(true);
      setIsPhoneModalOpen(false);
      toast.success("Phone number verified and updated!");
    } catch (error) {
      console.error("[VerifyOTP] Failed:", error);
      toast.error("OTP verification failed: " + (error.message || "Invalid OTP"));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const openPhoneModal = () => {
    setIsOtpSent(false);
    setPhoneOtp("");
    setIsPhoneModalOpen(true);
  };

  const closePhoneModal = () => {
    setIsOtpSent(false);
    setPhoneOtp("");
    setIsPhoneModalOpen(false);
  };

  // Display current phone
  // Format phone for display: "+91 9315592742"
  const displayPhone = (() => {
    if (formData.mobileWithCountryCode) {
      try {
        const parsed = parsePhoneNumber(formData.mobileWithCountryCode);
        if (parsed) return `+${parsed.countryCallingCode} ${parsed.nationalNumber}`;
      } catch {}
      return formData.mobileWithCountryCode;
    }
    if (formData.mobileNumber) return `${formData.countryCode || ""} ${formData.mobileNumber}`.trim();
    return "";
  })();

  // ---- Account Settings shell (light mockup layout) ----
  const [activeSection, setActiveSection] = useState("profile");
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [planName, setPlanName] = useState("");
  // Billing: active subscription (from subscriptions/{uid}.transactions[]) + paid history.
  const [billing, setBilling] = useState({ loading: true, active: null, history: [], daysLeft: 0 });
  const [showAllBilling, setShowAllBilling] = useState(false); // Billing History "View more" toggle
  const [paymentLoading, setPaymentLoading] = useState(false); // Razorpay checkout in progress
  const [billingRefresh, setBillingRefresh] = useState(0); // bump to re-fetch billing after payment

  useEffect(() => {
    if (!userId) return;
    featureGatingService.getPlanDisplayName(userId).then(setPlanName).catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "subscriptions", userId));
        const txns = snap.exists() ? snap.data().transactions || [] : [];
        const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
        const now = new Date();
        let active = null;
        txns.forEach((t) => {
          const end = toDate(t["end-time"]);
          const start = toDate(t["start-time"]);
          if (end && end > now && (!active || end > active.endDate)) {
            active = { tier: t.category || null, type: t.type || "free", startDate: start, endDate: end };
          }
        });
        const history = txns
          .filter((t) => (t.type || "") !== "free")
          .map((t) => ({ category: t.category || null, start: toDate(t["start-time"]), end: toDate(t["end-time"]) }))
          .sort((a, b) => (b.start?.getTime() || 0) - (a.start?.getTime() || 0));
        const daysLeft = active ? Math.max(0, Math.floor((active.endDate - now) / 86400000)) : 0;
        if (!cancelled) setBilling({ loading: false, active, history, daysLeft });
      } catch (e) {
        if (!cancelled) setBilling({ loading: false, active: null, history: [], daysLeft: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [userId, billingRefresh]);

  // Open the Razorpay checkout for the BASIC plan in-place (no page navigation).
  const handleUpgrade = async () => {
    if (paymentLoading) return;
    if (!userId) { toast.error("User not identified."); return; }
    const plan = SubscriptionPlansConfig.LAWYER_BASIC || {};
    try {
      setPaymentLoading(true);
      toast.info("Initiating payment...");
      const result = await processRazorpayPayment({
        amount: plan.priceINR,
        currency: "INR",
        userId,
        userEmail: formData.email || auth.currentUser?.email || "",
        userName: formData.name || "",
        userPhone: formData.mobileNumber || "",
        planDetails: {
          planType: "basic",
          duration: 1,
          category: plan.category,
        },
      });
      if (result.success) {
        toast.success("Payment successful! Your subscription has been activated.");
        setBillingRefresh((n) => n + 1);
      } else {
        toast.error(result.error || "Payment failed. Please try again.");
      }
    } catch (e) {
      console.error("Razorpay payment error:", e);
      toast.error("Payment failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // Change password (email/password accounts; Google sign-ins are told to use Google).
  const handleChangePassword = async () => {
    const user = auth.currentUser;
    if (!user?.email) { toast.error("No signed-in user."); return; }
    if (pwdForm.next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (pwdForm.next !== pwdForm.confirm) { toast.error("New passwords do not match."); return; }
    try {
      setIsUpdatingPassword(true);
      const cred = EmailAuthProvider.credential(user.email, pwdForm.current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, pwdForm.next);
      setPwdForm({ current: "", next: "", confirm: "" });
      toast.success("Password updated successfully!");
    } catch (e) {
      const msg =
        e.code === "auth/wrong-password" || e.code === "auth/invalid-credential"
          ? "Current password is incorrect."
          : e.code === "auth/too-many-requests"
            ? "Too many attempts - please try again later."
            : "Could not update password. If you signed in with Google, manage your password in your Google account.";
      toast.error(msg);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const SETTINGS_LABELS = {
    mobileNumber: "Show mobile number",
    email: "Show email address",
    lawyerId: "Show enrollment ID",
    location: "Show location",
  };

  const sections = ACCOUNT_SECTIONS.filter((s) => !(s.lawyerOnly && isClient));
  const activeLabel = sections.find((s) => s.key === activeSection)?.label || "Profile Information";
  const grantedOn = (() => {
    const v = formData.departmentVerifiedAt;
    try {
      const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
      return d && !isNaN(d.getTime())
        ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "";
    } catch {
      return "";
    }
  })();

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <div className="crm-compact min-h-screen bg-[#F8FAFC] text-gray-900">
        <main className="px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm">
            <Link to="/portal/dashboard" className="text-slate-500 hover:text-slate-800">Home</Link>
            <ChevronRightIcon className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500">Account</span>
            <ChevronRightIcon className="h-4 w-4 text-slate-400" />
            <span className="font-medium text-slate-800">{activeLabel}</span>
          </div>

          {/* Header + department verified chip */}
          <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Account Settings</h1>
              <p className="mt-0.5 text-xs text-slate-500">Manage your account preferences and security</p>
            </div>
            {!isClient && isDepartmentVerified && (
              <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                <div className="leading-tight">
                  <p className="m-0 text-sm font-semibold text-emerald-700">Department Verified</p>
                  {departmentName && <p className="m-0 text-xs text-emerald-600/80">{departmentName}</p>}
                </div>
              </div>
            )}
          </div>

          <div className="mt-5 grid grid-cols-1 items-start gap-4 lg:grid-cols-[230px_minmax(0,1fr)_280px]">
            {/* Section nav */}
            <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
              {sections.map(({ key, label, Icon }) => {
                const active = activeSection === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveSection(key)}
                    className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                      active
                        ? "bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-200"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Active section */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              {activeSection === "profile" && (
                <>
                  <h2 className="text-base font-bold text-slate-900">Profile Information</h2>
                  <p className="mt-0.5 text-xs text-slate-500">Manage your personal and professional details</p>

                  {/* Photo */}
                  <div className="mt-5">
                    <p className="mb-1 text-xs font-medium text-slate-500">Profile Photo</p>
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-indigo-600 text-lg font-bold text-white">
                        {formData.profile ? (
                          <img src={formData.profile} alt="Profile" className="h-full w-full object-cover" />
                        ) : (
                          (formData.name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase()
                        )}
                      </div>
                      <div>
                        <label htmlFor="profile-upload" className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                          <Camera className="h-4 w-4" /> Change Photo
                        </label>
                        <input type="file" id="profile-upload" accept="image/png,image/jpeg" onChange={handleImageUpload} className="hidden" />
                        <p className="mt-1 text-[11px] text-slate-400">JPG, PNG up to 2MB</p>
                      </div>
                    </div>
                  </div>

                  {/* Form grid */}
                  <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
                    <AccField label="Full Name">
                      <input name="name" value={formData.name || ""} onChange={handleChange} className={ACC_INPUT_CLS} />
                    </AccField>
                    {!isClient && (
                      <AccField label="User Type">
                        <SelectMenu name="userCategory" value={formData.userCategory || ""} onChange={handleChange} className={ACC_INPUT_CLS}>
                          <option value="">Select</option>
                          <option value="Lawyer">Lawyer</option>
                          <option value="Advocate">Advocate</option>
                          <option value="Law student">Law student</option>
                        </SelectMenu>
                      </AccField>
                    )}
                    <AccField label="Email Address">
                      <div className="relative">
                        <input value={formData.email || ""} disabled className={`${ACC_INPUT_CLS} cursor-not-allowed bg-slate-50 text-slate-500 ${isEmailVerified ? "pr-9" : ""}`} />
                        {isEmailVerified && <span className="absolute right-2.5 top-1/2 -translate-y-1/2"><BlueTick /></span>}
                      </div>
                    </AccField>
                    {!isClient && formData.userCategory !== "Law student" && (
                      <AccField label="Practicing Field">
                        <input name="type" value={formData.type || ""} onChange={handleChange} placeholder="e.g. Civil Law, Criminal Law" className={ACC_INPUT_CLS} />
                      </AccField>
                    )}
                    {!isClient && formData.userCategory === "Law student" && (
                      <AccField label="College Name">
                        <input name="collegeName" value={formData.collegeName || ""} onChange={handleChange} className={ACC_INPUT_CLS} />
                      </AccField>
                    )}
                    <AccField label="Phone Number">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <input value={displayPhone || ""} disabled className={`${ACC_INPUT_CLS} cursor-not-allowed bg-slate-50 text-slate-500 ${isPhoneVerified ? "pr-9" : ""}`} />
                          {isPhoneVerified && <span className="absolute right-2.5 top-1/2 -translate-y-1/2"><BlueTick /></span>}
                        </div>
                        <button type="button" onClick={openPhoneModal} title="Change phone number" className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 shadow-sm hover:bg-slate-50 hover:text-slate-800">
                          <PencilIcon />
                        </button>
                      </div>
                    </AccField>
                    {!isClient && (formData.userCategory === "Lawyer" || formData.userCategory === "Advocate") && (
                      <AccField label="Law/Professional Firm">
                        <div className="flex flex-col gap-1.5 pt-1">
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="radio" name="representsFirm" value="Yes" checked={formData.representsFirm === "Yes"} onChange={handleChange} className="accent-yellow-500" />
                            Yes, I represent a firm
                          </label>
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="radio" name="representsFirm" value="No" checked={formData.representsFirm === "No"} onChange={handleChange} className="accent-yellow-500" />
                            No, I practice independently
                          </label>
                        </div>
                      </AccField>
                    )}
                    {isClient && (
                      <AccField label="Representing a firm?">
                        <SelectMenu name="representsFirm" value={formData.representsFirm || ""} onChange={handleChange} className={ACC_INPUT_CLS}>
                          <option value="">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </SelectMenu>
                      </AccField>
                    )}
                    {!isClient && (
                      <AccField label="Law Degree">
                        <SelectMenu name="degree" value={formData.degree || ""} onChange={handleChange} className={ACC_INPUT_CLS}>
                          <option value="">Select</option>
                          <option value="B.Sc.LLB">B.Sc.LLB</option>
                          <option value="BBA.LLB">BBA.LLB</option>
                          <option value="B.Com.LLB">B.Com.LLB</option>
                          <option value="B.A.LLB">B.A.LLB</option>
                          <option value="LLB">LLB</option>
                          <option value="Other">Other</option>
                        </SelectMenu>
                      </AccField>
                    )}
                    {formData.representsFirm === "Yes" && (
                      <AccField label="Law/Professional Firm Name">
                        <input name="firmName" value={formData.firmName || ""} onChange={handleChange} className={ACC_INPUT_CLS} />
                      </AccField>
                    )}
                    {!isClient && (formData.userCategory === "Lawyer" || formData.userCategory === "Advocate") && (
                      <AccField label="Enrollment ID">
                        <input name="lawyerId" value={formData.lawyerId || ""} onChange={handleChange} className={ACC_INPUT_CLS} />
                      </AccField>
                    )}
                    {!isClient && formData.userCategory === "Law student" && (
                      <AccField label="Registration Number">
                        <input name="registrationNumber" value={formData.registrationNumber || ""} onChange={handleChange} className={ACC_INPUT_CLS} />
                      </AccField>
                    )}
                    <AccField label="Address">
                      <textarea name="address" rows={2} value={formData.address || ""} onChange={handleChange} className={ACC_INPUT_CLS} />
                    </AccField>
                    <AccField label="Location">
                      <input name="location" value={formData.location || ""} onChange={handleChange} className={ACC_INPUT_CLS} />
                    </AccField>
                    <div className="md:col-span-2">
                      <AccField label="About You">
                        <textarea name="about" rows={3} maxLength={200} value={formData.about || ""} onChange={handleChange} placeholder="A short professional summary..." className={ACC_INPUT_CLS} />
                      </AccField>
                      <p className="mt-1 text-right text-[11px] text-slate-400">{(formData.about || "").length}/200</p>
                    </div>
                  </div>

                  <div className="mt-5 flex justify-end">
                    <button onClick={handleSaveChanges} className="rounded-lg bg-yellow-500 px-5 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600">
                      Save Changes
                    </button>
                  </div>
                </>
              )}

              {activeSection === "preferences" && !isClient && (
                <>
                  <h2 className="text-base font-bold text-slate-900">Preferences</h2>
                  <p className="mt-0.5 text-xs text-slate-500">Choose what you want to show to other lawyers and users</p>
                  <div className="mt-5 space-y-3">
                    {Object.entries(settings).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <span className="text-sm font-medium text-slate-700">{SETTINGS_LABELS[key] || key}</span>
                        <AccToggle on={value} onClick={() => handleToggleSetting(key)} />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeSection === "security" && (
                <>
                  <h2 className="text-base font-bold text-slate-900">Security</h2>
                  <p className="mt-0.5 text-xs text-slate-500">Keep your account secure</p>

                  <div className="mt-5 max-w-sm space-y-4">
                    <h3 className="m-0 text-sm font-bold text-slate-900">Change Password</h3>
                    <AccField label="Current Password">
                      <input type="password" value={pwdForm.current} onChange={(e) => setPwdForm((p) => ({ ...p, current: e.target.value }))} className={ACC_INPUT_CLS} />
                    </AccField>
                    <AccField label="New Password">
                      <input type="password" value={pwdForm.next} onChange={(e) => setPwdForm((p) => ({ ...p, next: e.target.value }))} className={ACC_INPUT_CLS} />
                    </AccField>
                    <AccField label="Confirm New Password">
                      <input type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm((p) => ({ ...p, confirm: e.target.value }))} className={ACC_INPUT_CLS} />
                    </AccField>
                    <button
                      onClick={handleChangePassword}
                      disabled={isUpdatingPassword || !pwdForm.current || !pwdForm.next}
                      className="rounded-lg bg-yellow-500 px-5 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600 disabled:opacity-50"
                    >
                      {isUpdatingPassword ? "Updating..." : "Update Password"}
                    </button>
                    <p className="text-[11px] text-slate-400">Signed in with Google? Manage your password from your Google account instead.</p>
                  </div>

                  <div className="mt-6 border-t border-slate-100 pt-5">
                    <h3 className="m-0 text-sm font-bold text-slate-900">Account Actions</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={() => { logOut(); navigate("/portal/onboarding"); }} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                        Logout
                      </button>
                      <button onClick={openDeleteConfirmationModal} className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-50">
                        Delete Account
                      </button>
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">Once you delete your account, there is no going back. Please be certain.</p>
                  </div>
                </>
              )}

              {activeSection === "department" && !isClient && (
                <>
                  <h2 className="text-base font-bold text-slate-900">Access to Department</h2>
                  <p className="mt-0.5 text-xs text-slate-500">Department access managed by your organisation admin</p>
                  {isDepartmentVerified ? (
                    <div className="mt-5 rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="m-0 text-sm font-semibold text-slate-900">{departmentName || "Department"}</p>
                        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">Verified</span>
                      </div>
                      <div className="mt-3 space-y-2 text-sm">
                        {formData.verifiedOrganisation && (
                          <p className="m-0"><span className="text-slate-400">Organisation: </span><span className="font-medium text-slate-700">{formData.verifiedOrganisation}</span></p>
                        )}
                        <p className="m-0"><span className="text-slate-400">Role: </span><span className="font-medium text-slate-700">Department User</span></p>
                        <p className="m-0"><span className="text-slate-400">Access Level: </span><span className="font-medium text-slate-700">Full Access</span></p>
                        {grantedOn && <p className="m-0"><span className="text-slate-400">Granted On: </span><span className="font-medium text-slate-700">{grantedOn}</span></p>}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-5">
                      <p className="text-sm text-slate-600">Have a department access code from your organisation? Verify it to unlock department features.</p>
                      <button onClick={openDeptModal} className="mt-3 rounded-lg bg-yellow-500 px-5 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600">
                        Enter Access Code
                      </button>
                    </div>
                  )}
                </>
              )}

              {activeSection === "connected" && (
                <>
                  <h2 className="text-base font-bold text-slate-900">Connected Accounts</h2>
                  <p className="mt-0.5 text-xs text-slate-500">Sync Jurident with your other tools</p>
                  <div className="mt-5 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div>
                      <p className="m-0 text-sm font-medium text-slate-700">Google Calendar</p>
                      <p className="m-0 text-[11px] text-slate-400">Sync hearings and events to your Google Calendar</p>
                    </div>
                    <AccToggle on={googleca.googleCalender} onClick={handleGoogleCalendarToggle} />
                  </div>
                  <button onClick={() => navigate("/portal/googlecalendar")} className="mt-3 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                    Open Calendar Sync Settings
                  </button>
                </>
              )}

              {activeSection === "billing" && (() => {
                const currentCfg = billing.active ? SubscriptionPlansConfig.getPlanByCategory(billing.active.tier) || {} : {};
                const basicCfg = SubscriptionPlansConfig.LAWYER_BASIC || {};
                const enterpriseCfg = SubscriptionPlansConfig.LAWYER_ENTERPRISE || {};
                const freeCfg = (isClient ? SubscriptionPlansConfig.CLIENT_FREE : SubscriptionPlansConfig.LAWYER_FREE) || {};
                const currentFeatures = currentCfg.features?.length ? currentCfg.features : (freeCfg.features || []);
                const isPaid = !!billing.active;
                const isBasicCurrent = billing.active?.tier === "Lawyer-Basic";
                return (
                <>
                  <h2 className="text-base font-bold text-slate-900">Billing &amp; Plan</h2>
                  <p className="mt-0.5 text-xs text-slate-500">Manage your subscription and payments</p>

                  {/* Current Plan */}
                  <div className="mt-5 rounded-xl border border-slate-200 p-4">
                    <p className="m-0 text-xs font-medium text-slate-400">Current Plan</p>
                    <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="m-0 text-lg font-bold text-slate-900">{planName || "Free Plan"}</p>
                        <p className="m-0 text-xs text-slate-500">
                          {isPaid ? "All plan features unlocked" : "Free features included"}
                        </p>
                        {isPaid && (
                          <p className="m-0 mt-1 text-xs text-slate-500">
                            Next billing date: <span className="font-medium text-slate-700">{fmtBillingDate(billing.active.endDate)}</span>
                          </p>
                        )}
                      </div>
                      {currentCfg.priceDisplay && (
                        <div className="text-right">
                          <p className="m-0 text-lg font-bold text-slate-900">
                            {currentCfg.priceDisplay}
                            <span className="text-xs font-medium text-slate-400">{currentCfg.duration || ""}</span>
                          </p>
                        </div>
                      )}
                    </div>
                    {currentFeatures.length > 0 && (
                      <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                        {currentFeatures.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                            <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> {f}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Active subscription duration */}
                  {isPaid && (
                    <div className="mt-4 rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        <p className="m-0 text-sm font-bold text-slate-900">Active Subscription</p>
                      </div>
                      <p className="m-0 mt-1 text-xs text-slate-500">
                        {planName} — {billing.daysLeft} day{billing.daysLeft === 1 ? "" : "s"} left
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="m-0 flex items-center gap-1.5 text-[11px] text-slate-400"><CalendarDays className="h-3.5 w-3.5" /> Start Date</p>
                          <p className="m-0 mt-0.5 text-sm font-semibold text-slate-800">{fmtBillingDate(billing.active.startDate)}</p>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="m-0 flex items-center gap-1.5 text-[11px] text-slate-400"><CheckCircle2 className="h-3.5 w-3.5" /> End Date</p>
                          <p className="m-0 mt-0.5 text-sm font-semibold text-slate-800">{fmtBillingDate(billing.active.endDate)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Available plans (lawyers) */}
                  {!isClient && (
                    <div className="mt-4">
                      <p className="text-sm font-bold text-slate-900">Available Plans</p>
                      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="flex flex-col rounded-xl border border-slate-200 p-4">
                          <p className="text-center text-sm font-bold tracking-wide text-slate-500">{basicCfg.name || "BASIC"}</p>
                          <p className="mt-1 text-center text-2xl font-bold text-slate-900">
                            {basicCfg.priceDisplay || "₹249"}
                            <span className="text-sm font-medium text-slate-400">{basicCfg.duration || "/month"}</span>
                          </p>
                          <div className="mt-4 flex-1 space-y-2">
                            {(basicCfg.features || []).map((f, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-yellow-500" /> {f}
                              </div>
                            ))}
                          </div>
                          {isBasicCurrent ? (
                            <span className="mt-4 inline-flex self-center items-center justify-center rounded-lg bg-emerald-50 px-5 py-1.5 text-xs font-semibold text-emerald-600 ring-1 ring-inset ring-emerald-200">Current Plan</span>
                          ) : (
                            <button onClick={handleUpgrade} disabled={paymentLoading} className="mt-4 self-center rounded-lg bg-yellow-500 px-5 py-1.5 text-xs font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-60">{paymentLoading ? "Processing…" : "Upgrade"}</button>
                          )}
                        </div>
                        <div className="flex flex-col rounded-xl border border-slate-200 p-4">
                          <p className="text-center text-sm font-bold tracking-wide text-slate-500">{enterpriseCfg.name || "ENTERPRISE"}</p>
                          <p className="mt-1 text-center text-2xl font-bold text-slate-900">{enterpriseCfg.priceDisplay || "Custom Pricing"}</p>
                          <div className="mt-4 flex-1 space-y-2">
                            {(enterpriseCfg.features || []).map((f, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-yellow-500" /> {f}
                              </div>
                            ))}
                          </div>
                          <button onClick={() => navigate("/portal/subscription")} className="mt-4 self-center rounded-lg border border-yellow-400 bg-white px-5 py-1.5 text-xs font-semibold text-yellow-700 transition-colors hover:bg-yellow-50">Inquire</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Billing History */}
                  <div className="mt-4 rounded-xl border border-slate-200 p-4">
                    <p className="m-0 text-sm font-bold text-slate-900">Billing History</p>
                    {billing.loading ? (
                      <p className="m-0 mt-2 text-xs text-slate-400">Loading…</p>
                    ) : billing.history.length === 0 ? (
                      <p className="m-0 mt-2 text-xs text-slate-400">No billing history yet.</p>
                    ) : (
                      <>
                        <div className="mt-2 divide-y divide-slate-100">
                          {(showAllBilling ? billing.history : billing.history.slice(0, 5)).map((h, i) => {
                            const cfg = SubscriptionPlansConfig.getPlanByCategory(h.category) || {};
                            return (
                              <div key={i} className="flex items-center justify-between gap-3 py-2.5">
                                <div className="min-w-0">
                                  <p className="m-0 text-sm font-medium text-slate-800">{cfg.name ? `${cfg.name} Plan` : (h.category || "Plan")}</p>
                                  <p className="m-0 text-[11px] text-slate-400">{fmtBillingDate(h.start)}</p>
                                </div>
                                <p className="m-0 shrink-0 text-sm font-semibold text-slate-900">{cfg.priceDisplay || "—"}</p>
                              </div>
                            );
                          })}
                        </div>
                        {billing.history.length > 5 && (
                          <button
                            type="button"
                            onClick={() => setShowAllBilling((v) => !v)}
                            className="mt-2 text-xs font-semibold text-yellow-700 transition-colors hover:text-yellow-800"
                          >
                            {showAllBilling ? "View less" : `View more (${billing.history.length - 5})`}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
                );
              })()}
            </div>

            {/* Right rail */}
            <div className="space-y-4">
              {!isClient && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2">
                    <UsersIcon className="h-4 w-4 text-indigo-600" />
                    <h3 className="m-0 text-sm font-bold text-slate-900">Access to Department</h3>
                  </div>
                  {isDepartmentVerified ? (
                    <>
                      <p className="mt-1.5 text-xs text-slate-500">You have access to the department managed by admin.</p>
                      <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="m-0 text-xs font-semibold text-slate-800">{departmentName || "Department"}</p>
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">Verified</span>
                        </div>
                        <p className="m-0 mt-2 text-[11px] text-slate-400">Role<br /><span className="text-xs font-medium text-slate-700">Department User</span></p>
                        <p className="m-0 mt-2 text-[11px] text-slate-400">Access Level<br /><span className="text-xs font-medium text-slate-700">Full Access</span></p>
                        {grantedOn && <p className="m-0 mt-2 text-[11px] text-slate-400">Granted On<br /><span className="text-xs font-medium text-slate-700">{grantedOn}</span></p>}
                      </div>
                    </>
                  ) : (
                    <p className="mt-1.5 text-xs text-slate-500">Verify your organisation access code to unlock department features.</p>
                  )}
                  <button onClick={() => setActiveSection("department")} className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                    View Department Settings
                  </button>
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="m-0 text-sm font-bold text-slate-900">Need Help?</h3>
                <p className="mt-1.5 text-xs text-slate-500">If you face any issues with your account settings, our support team is here to help.</p>
                <button onClick={() => navigate("/portal/contact")} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                  <Headphones className="h-4 w-4" /> Contact Support
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Phone Edit Modal */}
      <PhoneEditModal
        isOpen={isPhoneModalOpen}
        onClose={closePhoneModal}
        newCountry={newCountry}
        setNewCountry={(c) => { setNewCountry(c); setIsOtpSent(false); }}
        newNationalNumber={newNationalNumber}
        setNewNationalNumber={(n) => { setNewNationalNumber(n); setIsOtpSent(false); }}
        phoneOtp={phoneOtp}
        setPhoneOtp={setPhoneOtp}
        isSendingOtp={isSendingOtp}
        isVerifyingOtp={isVerifyingOtp}
        isOtpSent={isOtpSent}
        otpE164Number={otpE164Number}
        onSendOtp={handleSendOtp}
        onVerifyOtp={handleVerifyOtp}
        onResendOtp={handleResendOtp}
        isNewPhoneValid={isNewPhoneValid}
      />

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteConfirmationModal}
        onConfirm={handleDeleteAccount}
        deleteReason={deleteReason}
        setDeleteReason={setDeleteReason}
        customReason={customReason}
        setCustomReason={setCustomReason}
        confirmText={confirmText}
        setConfirmText={setConfirmText}
      />

      {/* Department Access Modal */}
      <DepartmentAccessModal
        isOpen={isDeptModalOpen}
        onClose={closeDeptModal}
        code={deptCode}
        setCode={setDeptCode}
        onVerify={handleVerifyDeptCode}
        isVerifying={isDeptVerifying}
      />
    </>
  );
}

export default Account;
